import { useState } from "react";
import { BusinessSettingsLayout } from "@/components/business-settings/BusinessSettingsLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { useGeolocationSettings } from "@/hooks/useGeolocationSettings";
import { useToast } from "@/hooks/use-toast";
import { MapPin, Plus, Loader2 } from "lucide-react";
import { GeofenceList } from "@/components/geolocation/GeofenceList";
import { GeofenceDialog } from "@/components/geolocation/GeofenceDialog";
import { GeolocationLogsTable } from "@/components/geolocation/GeolocationLogsTable";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function GeolocationSettings() {
  const { settings, geofences, isLoading, filter, setGeofenceFilter, updateSettings, createGeofence, updateGeofence, deleteGeofence } = useGeolocationSettings();
  const { toast } = useToast();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingGeofence, setEditingGeofence] = useState<string | null>(null);

  const handleSettingChange = async (key: keyof typeof settings, value: boolean | number) => {
    try {
      await updateSettings({ [key]: value });
      toast({
        title: "Settings updated",
        description: "Geolocation settings have been saved.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update settings. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleRadiusChange = async (value: string) => {
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue) && numValue > 0) {
      await handleSettingChange('geo_default_radius_meters', numValue);
    }
  };

  const handleAddGeofence = () => {
    setEditingGeofence(null);
    setIsDialogOpen(true);
  };

  const handleEditGeofence = (id: string) => {
    setEditingGeofence(id);
    setIsDialogOpen(true);
  };

  const handleDeleteGeofence = async (id: string) => {
    try {
      await deleteGeofence(id);
      toast({
        title: "Location deactivated",
        description: "The location has been marked as inactive.",
      });
    } catch (error) {
      console.error("Error deleting geofence:", error);
      toast({
        title: "Error",
        description: "Failed to deactivate location. Please try again.",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return (
      <BusinessSettingsLayout
        title="Geolocation"
        description="Configure location-based time tracking for your team"
      >
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </BusinessSettingsLayout>
    );
  }

  return (
    <BusinessSettingsLayout
      title="Geolocation"
      description="Configure location-based time tracking for your team"
    >
      <Tabs defaultValue="settings" className="space-y-6">
        <TabsList>
          <TabsTrigger value="settings">Settings</TabsTrigger>
          <TabsTrigger value="logs">Activity Logs</TabsTrigger>
        </TabsList>

        <TabsContent value="settings" className="space-y-6">
          {/* Main Settings Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                Location Tracking Settings
              </CardTitle>
              <CardDescription>
                Control how location data is captured during clock in/out
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="geo-enabled">Enable Geolocation</Label>
                  <p className="text-sm text-muted-foreground">
                    Require employees to share their location when clocking in/out
                  </p>
                </div>
                <Switch
                  id="geo-enabled"
                  checked={settings.geo_enabled}
                  onCheckedChange={(checked) => handleSettingChange('geo_enabled', checked)}
                />
              </div>

              {settings.geo_enabled && (
                <>
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="geo-strict">Strict Mode</Label>
                      <p className="text-sm text-muted-foreground">
                        Block clock in/out if employee is outside designated locations
                      </p>
                    </div>
                    <Switch
                      id="geo-strict"
                      checked={settings.geo_strict_mode}
                      onCheckedChange={(checked) => handleSettingChange('geo_strict_mode', checked)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="default-radius">Default Radius (meters)</Label>
                    <Input
                      id="default-radius"
                      type="number"
                      min="10"
                      max="5000"
                      value={settings.geo_default_radius_meters}
                      onChange={(e) => handleRadiusChange(e.target.value)}
                      className="max-w-xs"
                    />
                    <p className="text-sm text-muted-foreground">
                      Default geofence radius for new locations (10-5000 meters)
                    </p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Geofences Card */}
          {settings.geo_enabled && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Approved Locations</CardTitle>
                    <CardDescription>
                      Define where employees can clock in and out
                    </CardDescription>
                  </div>
                  <Button onClick={handleAddGeofence} size="sm">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Location
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Button
                    variant={filter === 'active' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setGeofenceFilter('active')}
                  >
                    Active
                  </Button>
                  <Button
                    variant={filter === 'inactive' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setGeofenceFilter('inactive')}
                  >
                    Inactive
                  </Button>
                  <Button
                    variant={filter === 'all' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setGeofenceFilter('all')}
                  >
                    All
                  </Button>
                </div>
                <GeofenceList
                  geofences={geofences}
                  onEdit={handleEditGeofence}
                  onDelete={handleDeleteGeofence}
                />
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="logs">
          <Card>
            <CardHeader>
              <CardTitle>Geolocation Activity Logs</CardTitle>
              <CardDescription>
                Review employee location data captured during clock in/out events
              </CardDescription>
            </CardHeader>
            <CardContent>
              <GeolocationLogsTable />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <GeofenceDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        geofence={editingGeofence ? geofences.find(g => g.id === editingGeofence) : undefined}
        defaultRadius={settings.geo_default_radius_meters}
        onSave={async (data) => {
          try {
            if (editingGeofence) {
              await updateGeofence(editingGeofence, data);
              toast({
                title: "Location updated",
                description: "The location has been updated successfully.",
              });
            } else {
              await createGeofence(data);
              toast({
                title: "Location added",
                description: "The new location has been added successfully.",
              });
            }
            setIsDialogOpen(false);
          } catch (error) {
            toast({
              title: "Error",
              description: "Failed to save location. Please try again.",
              variant: "destructive",
            });
          }
        }}
      />
    </BusinessSettingsLayout>
  );
}
